import { Icon } from '@eventbrite/eds-icon';
import { MinusChunky, PlusChunky } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, { FunctionComponent, KeyboardEvent } from 'react';
import './stepper.scss';
import { StepperButton } from './StepperButton';

export interface StepperProps {
    id: string;
    minValue: number;
    maxValue: number;
    totalQuantity: number;
    onIncreaseOneStep: () => void;
    onDecreaseOneStep: () => void;
    onSetQuantity: (value: number) => void;
    isDisabled?: boolean;
    increaseButtonContentNode?: React.ReactNode;
    decreaseButtonContentNode?: React.ReactNode;
}

/**
 * This component draw a 2 buttons for manage increase and decrease quantitys, also a quantity display
 * can be enabled or disabled
 * It require 2 methods to handle this actions (the quantity need to be part of the state)
 *
 * TODO: add accesibility: https://eventbrite.atlassian.net/browse/EB-200213
 */
export const Stepper: FunctionComponent<StepperProps> = ({
    id,
    minValue,
    maxValue,
    totalQuantity,
    onIncreaseOneStep,
    onDecreaseOneStep,
    onSetQuantity,
    isDisabled = false,
    increaseButtonContentNode,
    decreaseButtonContentNode,
}) => {
    const isAddButtonDisable = isDisabled || totalQuantity >= maxValue;

    const isRemoveButtonDisable = isDisabled || totalQuantity <= minValue;

    const addIconColor = isAddButtonDisable ? 'grey-500' : 'white';
    const removeIconColor = isRemoveButtonDisable ? 'grey-500' : 'white';

    const quantityColor = isDisabled ? 'ui-500' : 'ui-900';
    const quantityClassName = classNames(
        'eds-text-bl eds-mar-hor-4 eds-stepper__quantity-label',
        {
            [`eds-text-color--${quantityColor}`]: quantityColor,
        },
    );

    const stepperClassname = classNames(
        'eds-stepper eds-align--center-vertical',
    );

    const addDataName = `eds-stepper-${id}-increase-button`;
    const removeDataName = `eds-stepper-${id}-decrease-button`;
    const dataTestidQuantity = `eds-stepper-${id}-quantity`;

    const addIcon = increaseButtonContentNode ? (
        increaseButtonContentNode
    ) : (
        <Icon type={<PlusChunky />} color={addIconColor} />
    );
    const removeIcon = decreaseButtonContentNode ? (
        decreaseButtonContentNode
    ) : (
        <Icon type={<MinusChunky />} color={removeIconColor} />
    );

    const handleOnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case 'ArrowRight':
                return onIncreaseOneStep();
            case 'ArrowLeft':
                return onDecreaseOneStep();
            case 'Home':
                return onSetQuantity(minValue);
            case 'End':
                return onSetQuantity(maxValue);
            default:
                break;
        }
    };

    return (
        <div
            className={stepperClassname}
            data-testid="eds-stepper"
            onKeyDown={handleOnKeyDown}
        >
            <StepperButton
                dataTestid={removeDataName}
                isDisabled={isRemoveButtonDisable}
                onClick={onDecreaseOneStep}
                ariaLabel={gettext('Decrease').toString()}
            >
                {removeIcon}
            </StepperButton>

            <div className={quantityClassName} data-testid={dataTestidQuantity}>
                {totalQuantity}
            </div>

            <StepperButton
                dataTestid={addDataName}
                isDisabled={isAddButtonDisable}
                onClick={onIncreaseOneStep}
                ariaLabel={gettext('Increase').toString()}
            >
                {addIcon}
            </StepperButton>
        </div>
    );
};
